import { motion } from "framer-motion";
import { Check, ArrowRight, Globe, Play } from "lucide-react";
import { SERVICES, scrollToId } from "@/lib/site";
import { Reveal, SectionLabel } from "@/components/primitives";

export default function Services() {
  return (
    <section id="services" className="relative py-20 sm:py-28 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel color="#FF6B35">Services</SectionLabel>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
            What we build.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Three focused services designed to help your business look better, communicate better
            and work smarter.
          </p>
        </Reveal>

        <div className="mt-16 space-y-20 lg:space-y-28">
          {SERVICES.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={s.id} delay={0.05}>
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16" data-testid={`service-row-${s.id}`}>
                  {/* Copy */}
                  <div className={reversed ? "lg:order-2" : ""}>
                    <div className="flex items-center gap-4">
                      <span
                        className="font-display text-6xl font-extrabold leading-none sm:text-7xl"
                        style={{ color: s.accent }}
                      >
                        {s.num}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                        {s.badge}
                      </span>
                    </div>
                    <h3 className="mt-5 font-display text-3xl font-bold tracking-tight text-[#111827] sm:text-4xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 max-w-md text-base text-slate-600">{s.description}</p>

                    <ul className="mt-6 flex flex-wrap gap-2.5">
                      {s.features.map((f) => (
                        <li
                          key={f}
                          className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3.5 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-slate-100"
                        >
                          <Check className="h-3.5 w-3.5" style={{ color: s.accent }} />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => scrollToId("contact")}
                      className="group/btn mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                      style={{ background: s.accent }}
                      data-testid={`service-cta-${s.id}`}
                    >
                      {s.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                    </button>
                  </div>

                  {/* Visual */}
                  <div className={reversed ? "lg:order-1" : ""}>
                    <ServiceVisual service={s} />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServiceVisual({ service }) {
  const { id, accent, accent2, tint } = service;

  return (
    <div className="relative">
      {/* backing shape */}
      <div
        className="absolute -inset-4 -z-10 rounded-[2.5rem]"
        style={{ background: tint }}
      />
      <div
        className="absolute -right-4 -top-4 -z-10 h-24 w-24 rounded-full opacity-80 blur-xl"
        style={{ background: accent }}
      />

      {id === "web-dev" && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-float ring-1 ring-slate-100">
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF6B35]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FFC94A]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#65A30D]" />
            <span className="ml-3 flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
              <Globe className="h-3 w-3 text-slate-400" />
              <span className="text-[10px] text-slate-400">yourbrand.com</span>
            </span>
          </div>
          <div className="p-6">
            <div className="h-3 w-24 rounded-full" style={{ background: accent }} />
            <div className="mt-3 h-7 w-3/4 rounded-lg bg-slate-200" />
            <div className="mt-2 h-7 w-1/2 rounded-lg bg-slate-100" />
            <div className="mt-5 flex gap-3">
              <div className="h-9 w-28 rounded-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent2})` }} />
              <div className="h-9 w-20 rounded-full ring-1 ring-slate-200" />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[0, 1, 2].map((n) => (
                <motion.div
                  key={n}
                  className="h-20 rounded-xl bg-slate-50 ring-1 ring-slate-100"
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2.4, repeat: Infinity, delay: n * 0.3 }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {id === "ai-video" && (
        <div className="flex items-center justify-center gap-4 rounded-2xl bg-white p-6 shadow-float ring-1 ring-slate-100">
          {[0, 1, 2].map((n) => (
            <motion.div
              key={n}
              className="relative aspect-[9/16] w-1/3 overflow-hidden rounded-xl"
              style={{
                background: [
                  "linear-gradient(160deg,#FF6B35,#FF9F6B)",
                  "linear-gradient(160deg,#7C3AED,#C026D3)",
                  "linear-gradient(160deg,#FF6B35,#7C3AED)",
                ][n],
              }}
              animate={{ y: [0, n === 1 ? -8 : 6, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: n * 0.4, ease: "easeInOut" }}
            >
              <div className="absolute left-1.5 top-1.5 rounded-full bg-white/25 px-1.5 py-0.5 text-[7px] font-bold uppercase text-white backdrop-blur-sm">
                Ad {n + 1}
              </div>
              {n === 1 && (
                <div className="absolute left-1/2 top-1/2 grid h-8 w-8 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white">
                  <Play className="h-3.5 w-3.5 translate-x-[1px] fill-[#7C3AED] text-[#7C3AED]" />
                </div>
              )}
              <div className="absolute inset-x-1.5 bottom-2 h-1 rounded-full bg-white/40">
                <div className="h-full w-2/3 rounded-full bg-white" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {id === "ai-automation" && (
        <div className="rounded-2xl bg-white p-6 shadow-float ring-1 ring-slate-100">
          <div className="flex flex-col gap-2.5">
            {[
              { label: "Lead", c: "#2563EB" },
              { label: "AI", c: "#7C3AED" },
              { label: "Decision", c: "#FF6B35" },
              { label: "Action", c: "#65A30D" },
              { label: "Result", c: "#2563EB" },
            ].map((node, i, arr) => (
              <div key={node.label}>
                <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5 ring-1 ring-slate-100">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: node.c }} />
                  <span className="text-sm font-semibold text-[#111827]">{node.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className="ml-[21px] h-3 w-px bg-slate-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
